import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  CalendarDays, 
  Users, 
  MapPin, 
  Star, 
  Bed, 
  Bath, 
  Wifi, 
  Car,
  Coffee,
  Waves,
  CheckCircle,
  Clock,
  CreditCard,
  Shield,
  Phone,
  Mail,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { mockProperties, mockAvailableDates } from '../mock';
import { format, differenceInDays, addDays, isSameDay } from 'date-fns';

const Booking = () => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState(1);
  const [step, setStep] = useState(1); // 1: Property Selection, 2: Dates & Guests, 3: Guest Info, 4: Confirmation
  const [guestInfo, setGuestInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: '',
    purpose: ''
  });
  const [bookingConfirmed, setBookingConfirmed] = useState(false);

  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
    setStep(2);
  };

  const handleDateSelection = () => {
    if (!checkIn || !checkOut) {
      toast.error('Please select both check-in and check-out dates.');
      return;
    }
    if (differenceInDays(checkOut, checkIn) < 1) {
      toast.error('Minimum stay is 1 night.');
      return;
    }
    setStep(3);
  };

  const handleGuestInfoSubmit = () => {
    if (!guestInfo.firstName || !guestInfo.lastName || !guestInfo.email) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setStep(4);
  };

  const handleBookingConfirmation = () => {
    // In a real app, this would process payment and create booking
    setBookingConfirmed(true);
    toast.success('Booking confirmed! Check your email for confirmation details.');
  };

  const isDateAvailable = (date) => {
    if (!selectedProperty) return false;
    const availableDates = mockAvailableDates[selectedProperty.id] || [];
    return availableDates.some(availableDate => 
      isSameDay(new Date(availableDate), date)
    );
  };

  const getTotalCost = () => {
    if (!selectedProperty || !checkIn || !checkOut) return 0;
    const nights = differenceInDays(checkOut, checkIn);
    const subtotal = nights * selectedProperty.price;
    const serviceFee = Math.round(subtotal * 0.12); // 12% service fee
    const taxes = Math.round(subtotal * 0.08); // 8% taxes
    return {
      nights,
      subtotal,
      serviceFee,
      taxes,
      total: subtotal + serviceFee + taxes
    };
  };

  const amenityIcons = {
    'WiFi': Wifi,
    'Kitchen': Coffee,
    'Air Conditioning': Waves,
    'Pool': Waves,
    'Gym': Users,
    'Garden': Users,
    'Security': Shield,
    'Parking': Car,
    'Workspace': Coffee,
    'Rooftop Access': Users
  };

  if (bookingConfirmed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-20">
        <Card className="max-w-2xl mx-auto shadow-xl">
          <CardContent className="p-12 text-center">
            <div className="bg-green-100 p-6 rounded-full w-fit mx-auto mb-8">
              <CheckCircle className="h-16 w-16 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Booking Confirmed!</h1>
            <p className="text-xl text-gray-600 mb-8">
              Your reservation at <strong>{selectedProperty?.title}</strong> has been confirmed.
            </p>
            <div className="bg-gray-50 p-6 rounded-lg mb-8">
              <div className="grid grid-cols-2 gap-4 text-left">
                <div>
                  <p className="text-sm text-gray-600">Check-in</p>
                  <p className="font-semibold">{checkIn ? format(checkIn, 'MMMM dd, yyyy') : ''}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Check-out</p>
                  <p className="font-semibold">{checkOut ? format(checkOut, 'MMMM dd, yyyy') : ''}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Guests</p>
                  <p className="font-semibold">{guests} {guests === 1 ? 'guest' : 'guests'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Cost</p>
                  <p className="font-semibold text-emerald-600">${getTotalCost().total}</p>
                </div>
              </div>
            </div>
            <p className="text-gray-600 mb-8">
              A confirmation email has been sent to <strong>{guestInfo.email}</strong>.
              Our team will contact you 24 hours before check-in with detailed instructions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => {
                setStep(1);
                setSelectedProperty(null);
                setBookingConfirmed(false);
                setCheckIn(null);
                setCheckOut(null);
                setGuests(1);
                setGuestInfo({
                  firstName: '',
                  lastName: '',
                  email: '',
                  phone: '',
                  specialRequests: '',
                  purpose: ''
                });
              }}>
                Book Another Property
              </Button>
              <Button variant="outline">
                <Mail className="mr-2 h-4 w-4" />
                View Confirmation Email
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Bar */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= stepNumber 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {stepNumber}
                </div>
                {stepNumber < 4 && (
                  <div className={`w-16 h-1 mx-2 ${
                    step > stepNumber ? 'bg-emerald-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-600">
            <span>Select Property</span>
            <span>Choose Dates</span>
            <span>Guest Details</span>
            <span>Confirmation</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Inquiry Form */}
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-light text-gray-900 mb-6 leading-tight">
              Property Inquiry
            </h1>
            <p className="text-xl text-gray-600 font-light">
              Contact us for exclusive access to our premium property portfolio
            </p>
          </div>

          <Card className="shadow-xl border-0">
            <CardHeader className="bg-emerald-600 text-white text-center rounded-t-lg">
              <CardTitle className="text-2xl font-light">
                <Mail className="inline-block mr-3 h-6 w-6" />
                Property Information Request
              </CardTitle>
            </CardHeader>
            <CardContent className="p-12">
              <form className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Full Name *
                    </label>
                    <Input
                      placeholder="Enter your full name"
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      placeholder="your.email@example.com"
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Phone Number
                    </label>
                    <Input
                      placeholder="+1 (555) 123-4567"
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Preferred Location
                    </label>
                    <Select>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select preferred location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="usa">United States</SelectItem>
                        <SelectItem value="uae">United Arab Emirates</SelectItem>
                        <SelectItem value="africa">Africa</SelectItem>
                        <SelectItem value="any">Any Location</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Check-in Date (Approx)
                    </label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full h-12 justify-start text-left font-light">
                          <CalendarDays className="mr-3 h-4 w-4" />
                          Select date
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Duration
                    </label>
                    <Select>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Length of stay" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-3">1-3 nights</SelectItem>
                        <SelectItem value="4-7">4-7 nights</SelectItem>
                        <SelectItem value="1-2weeks">1-2 weeks</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="extended">Extended stay</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Number of Guests
                    </label>
                    <Select>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="How many guests?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 Guest</SelectItem>
                        <SelectItem value="2">2 Guests</SelectItem>
                        <SelectItem value="4">4 Guests</SelectItem>
                        <SelectItem value="6">6 Guests</SelectItem>
                        <SelectItem value="8+">8+ Guests</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-light text-gray-700 mb-3">
                    Special Requirements & Preferences
                  </label>
                  <Textarea
                    placeholder="Please describe your preferences, special requirements, or any specific amenities you're looking for..."
                    rows={5}
                    className="resize-none"
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-light py-6"
                  onClick={(e) => {
                    e.preventDefault();
                    toast.success('Inquiry submitted! Our team will contact you within 24 hours.');
                  }}
                >
                  Submit Inquiry
                  <ArrowRight className="ml-3 h-5 w-5" />
                </Button>
              </form>

              <div className="mt-8 text-center">
                <p className="text-sm text-gray-600 font-light">
                  Our hospitality specialists will contact you within 24 hours with available options and exclusive rates.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Removed - now using inquiry form above */}
        {false && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-center mb-6">
                <Button variant="ghost" onClick={() => setStep(1)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Properties
                </Button>
              </div>
              
              <Card className="mb-6">
                <CardContent className="p-6">
                  <div className="flex space-x-4">
                    <img 
                      src={selectedProperty.images[0]} 
                      alt={selectedProperty.title}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-1">{selectedProperty.title}</h3>
                      <div className="flex items-center text-gray-600 mb-2">
                        <MapPin className="h-4 w-4 mr-1" />
                        <span className="text-sm">{selectedProperty.location}</span>
                      </div>
                      <div className="flex items-center">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="text-sm text-gray-600 ml-1">
                          {selectedProperty.rating} ({selectedProperty.reviews} reviews)
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Select Your Dates</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Check-in Date</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left h-12">
                            <CalendarDays className="mr-2 h-4 w-4" />
                            {checkIn ? format(checkIn, 'MMMM dd, yyyy') : 'Select check-in date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={checkIn}
                            onSelect={setCheckIn}
                            disabled={(date) => 
                              date < new Date() || 
                              !isDateAvailable(date) ||
                              (checkOut && date >= checkOut)
                            }
                            className="rounded-md border"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Check-out Date</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left h-12">
                            <CalendarDays className="mr-2 h-4 w-4" />
                            {checkOut ? format(checkOut, 'MMMM dd, yyyy') : 'Select check-out date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={checkOut}
                            onSelect={setCheckOut}
                            disabled={(date) => 
                              date < new Date() || 
                              !checkIn || 
                              date <= checkIn ||
                              !isDateAvailable(addDays(checkIn, differenceInDays(date, checkIn)))
                            }
                            className="rounded-md border"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Number of Guests</label>
                    <Select value={guests.toString()} onValueChange={(value) => setGuests(parseInt(value))}>
                      <SelectTrigger className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({length: selectedProperty.guests}, (_, i) => i + 1).map(num => (
                          <SelectItem key={num} value={num.toString()}>
                            {num} {num === 1 ? 'Guest' : 'Guests'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button 
                    onClick={handleDateSelection}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    size="lg"
                  >
                    Continue to Guest Details
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </div>
            
            <div>
              <Card className="sticky top-6">
                <CardHeader>
                  <CardTitle>Booking Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  {checkIn && checkOut && (
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span>Check-in:</span>
                        <span>{format(checkIn, 'MMM dd')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Check-out:</span>
                        <span>{format(checkOut, 'MMM dd')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Nights:</span>
                        <span>{getTotalCost().nights}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Guests:</span>
                        <span>{guests}</span>
                      </div>
                      <hr />
                      <div className="flex justify-between">
                        <span>${selectedProperty.price} x {getTotalCost().nights} nights</span>
                        <span>${getTotalCost().subtotal}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Service fee</span>
                        <span>${getTotalCost().serviceFee}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Taxes</span>
                        <span>${getTotalCost().taxes}</span>
                      </div>
                      <hr />
                      <div className="flex justify-between font-semibold text-lg">
                        <span>Total</span>
                        <span className="text-emerald-600">${getTotalCost().total}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 3: Guest Information */}
        {step === 3 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-center mb-6">
                <Button variant="ghost" onClick={() => setStep(2)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dates
                </Button>
              </div>
              
              <Card>
                <CardHeader>
                  <CardTitle>Guest Information</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <form className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                        <Input
                          required
                          value={guestInfo.firstName}
                          onChange={(e) => setGuestInfo({...guestInfo, firstName: e.target.value})}
                          placeholder="Enter first name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                        <Input
                          required
                          value={guestInfo.lastName}
                          onChange={(e) => setGuestInfo({...guestInfo, lastName: e.target.value})}
                          placeholder="Enter last name"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                        <Input
                          type="email"
                          required
                          value={guestInfo.email}
                          onChange={(e) => setGuestInfo({...guestInfo, email: e.target.value})}
                          placeholder="your.email@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                        <Input
                          value={guestInfo.phone}
                          onChange={(e) => setGuestInfo({...guestInfo, phone: e.target.value})}
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Purpose of Stay</label>
                      <Select value={guestInfo.purpose} onValueChange={(value) => setGuestInfo({...guestInfo, purpose: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select purpose of stay" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="leisure">Leisure/Vacation</SelectItem>
                          <SelectItem value="business">Business Travel</SelectItem>
                          <SelectItem value="family">Family Visit</SelectItem>
                          <SelectItem value="event">Special Event</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Special Requests</label>
                      <Textarea
                        value={guestInfo.specialRequests}
                        onChange={(e) => setGuestInfo({...guestInfo, specialRequests: e.target.value})}
                        placeholder="Any special requests, dietary restrictions, accessibility needs, etc."
                        rows={4}
                      />
                    </div>
                  </form>
                  
                  <Button 
                    onClick={handleGuestInfoSubmit}
                    className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700"
                    size="lg"
                  >
                    Continue to Confirmation
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </div>
            
            <div>
              <Card className="sticky top-6">
                <CardHeader>
                  <CardTitle>Booking Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span>Property:</span>
                      <span className="text-right text-sm">{selectedProperty?.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Dates:</span>
                      <span className="text-right text-sm">
                        {checkIn && checkOut && `${format(checkIn, 'MMM dd')} - ${format(checkOut, 'MMM dd')}`}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Guests:</span>
                      <span>{guests}</span>
                    </div>
                    <hr />
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Total</span>
                      <span className="text-emerald-600">${getTotalCost().total}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && (
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center mb-6">
              <Button variant="ghost" onClick={() => setStep(3)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guest Info
              </Button>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-center text-2xl">Confirm Your Booking</CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Property Details</h3>
                    <div className="space-y-3">
                      <div>
                        <span className="text-gray-600">Property:</span>
                        <p className="font-medium">{selectedProperty?.title}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Location:</span>
                        <p className="font-medium">{selectedProperty?.location}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Check-in:</span>
                        <p className="font-medium">{checkIn ? format(checkIn, 'MMMM dd, yyyy') : ''}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Check-out:</span>
                        <p className="font-medium">{checkOut ? format(checkOut, 'MMMM dd, yyyy') : ''}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Guests:</span>
                        <p className="font-medium">{guests} {guests === 1 ? 'guest' : 'guests'}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Guest Information</h3>
                    <div className="space-y-3">
                      <div>
                        <span className="text-gray-600">Name:</span>
                        <p className="font-medium">{guestInfo.firstName} {guestInfo.lastName}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Email:</span>
                        <p className="font-medium">{guestInfo.email}</p>
                      </div>
                      {guestInfo.phone && (
                        <div>
                          <span className="text-gray-600">Phone:</span>
                          <p className="font-medium">{guestInfo.phone}</p>
                        </div>
                      )}
                      {guestInfo.purpose && (
                        <div>
                          <span className="text-gray-600">Purpose:</span>
                          <p className="font-medium">{guestInfo.purpose}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {guestInfo.specialRequests && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-2">Special Requests</h3>
                    <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{guestInfo.specialRequests}</p>
                  </div>
                )}
                
                <div className="mt-8 bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4">Cost Breakdown</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>${selectedProperty?.price} x {getTotalCost().nights} nights</span>
                      <span>${getTotalCost().subtotal}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Service fee</span>
                      <span>${getTotalCost().serviceFee}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Taxes</span>
                      <span>${getTotalCost().taxes}</span>
                    </div>
                    <hr />
                    <div className="flex justify-between font-semibold text-xl">
                      <span>Total</span>
                      <span className="text-emerald-600">${getTotalCost().total}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 bg-blue-50 p-6 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Shield className="h-6 w-6 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2">Booking Protection</h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Free cancellation up to 24 hours before check-in</li>
                        <li>• 24/7 guest support during your stay</li>
                        <li>• Property verified and professionally managed</li>
                        <li>• Secure payment processing</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 text-center">
                  <Button 
                    onClick={handleBookingConfirmation}
                    size="lg"
                    className="bg-emerald-600 hover:bg-emerald-700 px-12 py-6 text-lg"
                  >
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Confirm Booking
                  </Button>
                  <p className="text-sm text-gray-600 mt-4">
                    By confirming, you agree to our terms of service and cancellation policy.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Booking;